import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ArrayUtils } from '../../../../shared/utils/array.util';
import { IPagination } from '../../../../shared/interfaces/base.interface';
import { UserPreferenceEnum } from '../enums/user-preference.enum';
import cities from '../../../../assets/jsons/city.json';
import countries from '../../../../assets/jsons/country.json';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly USER_END_POINT = `${environment.apiUrl}/v1/users`;
  private readonly USER_AUTH_END_POINT = `${environment.apiUrl}/v1/auth`;

  private _countries = [];
  private _dropdownCountries = [];
  private _cities: {
    id: string,
    name: string,
    zipCode: string,
  }[] = [];
  private _dropdownCities = [];

  constructor(private readonly http: HttpClient) {}

  public getUsers(input?) {
    return this.http.get<IPagination<any>>(`${this.USER_END_POINT}`, {
      params: input,
    });
  }

  public getUser(id) {
    return this.http.get<IPagination<any>>(`${this.USER_END_POINT}/${id}`);
  }

  public getUserPreference(input: { type: UserPreferenceEnum }) {
    return this.http.get<any>(`${this.USER_END_POINT}/preference`, {
      params: input,
    });
  }

  public setUserPreference(input: { type: string, data: object }) {
    return this.http.post(`${this.USER_END_POINT}/preference`, input);
  }

  public getReviewUserEdition(id) {
    return this.http.get<IPagination<any>>(`${this.USER_END_POINT}/review-user-edition/${id}`);
  }

  public create(input) {
    return this.http.post(`${this.USER_END_POINT}/create`, input);
  }

  public isEmailExisted(email: string) {
    return this.http.get(
      `${this.USER_AUTH_END_POINT}/is-email-existed/${email}`
    );
  }

  public update(id, input) {
    return this.http.post(
      `${this.USER_END_POINT}/update/${id}`,
      input
    );
  }

  public reviewCreation(id, input) {
    return this.http.post(
      `${this.USER_END_POINT}/review-user-creation/${id}`,
      input
    );
  }

  public reviewEdition(id, input, type) {
    return this.http.post(
      `${this.USER_END_POINT}/review-user-edition${type ? '/' + type : ''}/${id}`,
      input
    );
  }

  public sendResetPassword(id: string) {
    return this.http.post(
      `${this.USER_END_POINT}/send-reset-password/${id}`,
      {}
    );
  }

  public activate(id: string) {
    return this.http.post(
      `${this.USER_END_POINT}/activate/${id}`,
      {}
    );
  }

  public deactivate(id: string) {
    return this.http.post(
      `${this.USER_END_POINT}/deactivate/${id}`,
      {}
    );
  }

  public pendingCreation(id: string) {
    return this.http.post(
      `${this.USER_END_POINT}/pending-creation/${id}`,
      {}
    );
  }

  public sendInvitation(id: string) {
    return this.http.post(
      `${this.USER_END_POINT}/send-invitation/${id}`,
      {}
    );
  }

  public resendRejectEmail(id: string) {
    return this.http.post(
      `${this.USER_END_POINT}/resend/${id}`,
      {}
    );
  }

  public archive(id: string) {
    return this.http.post(
      `${this.USER_END_POINT}/archive/${id}`,
      {}
    );
  }

  public delete(id: string) {
    return this.http.post(`${this.USER_END_POINT}/delete/${id}`, {});
  }

  public cancelInvitation(id: string) {
    return this.http.post(
      `${this.USER_END_POINT}/cancel-invitation/${id}`,
      {}
    );
  }

  public getCountryByCode(countryCode: string) {
    return this._countries.find(x => x.id === countryCode);
  }

  public getCountriesDropdown() {
    if (this._dropdownCountries.length > 0) {
      return of(this._dropdownCountries);
    }
    Object.keys(countries).forEach((key) => {
      const country = countries[key];
      if (country) {
        this._countries.push({
          id: key,
          name: country.name,
        });
      }
    });
    this._dropdownCountries = this.formatToDropdownOptions(this._countries);
    this._dropdownCountries = ArrayUtils.sortArrayByField(
      this._dropdownCountries,
      'label'
    );
    return of(this._dropdownCountries);
  }

  public getCityByCode(cityCode: string) {
    return this._cities.find(x => x.id === cityCode);
  }

  public getCitiesDropdown() {
    if (this._dropdownCities.length > 0) {
      return of(this._dropdownCities);
    }
    Object.keys(cities).forEach((key) => {
      const city = cities[key];
      if (city) {
        this._cities.push({
          id: key,
          name: city.nameCity,
          zipCode: city.zipCode,
        });
      }
    });
    this._dropdownCities = this.formatToDropdownOptions(this._cities);
    this._dropdownCities = ArrayUtils.sortArrayByField(this._dropdownCities, 'label');
    return of(this._dropdownCities);
  }

  public formatToDropdownOptions(values: any[]) {
    return values.map((v) => ({
      value: v.id,
      label: v.name,
    }));
  }

  public exportUser(input?) {
    return this.http.get<any>(`${this.USER_END_POINT}/export`, {
      params: input,
    });
  }

  public exportAdministrativeUser(input?) {
    return this.http.get<any>(`${this.USER_END_POINT}/export-administrative-user`, {
      params: input,
    });
  }

  public sendReminder() {
    return this.http.get<any>(`${this.USER_END_POINT}/notification`);
  }

  public getImpersonationToken(id: string) {
    return this.http.post<{ token: string }>(`${this.USER_END_POINT}/get-impersonation-token/${id}`, {});
  }

  public getImpersonationLink(id: string) {
    return this.http.post<{ link: string }>(`${this.USER_END_POINT}/get-impersonation-link/${id}`, {});
  }

  public generateSmsCode(id: string) {
    return this.http.post<{ code: string; message: string }>(`${this.USER_END_POINT}/generate-sms-code/${id}`, {});
  }
}
